import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { KPI } from '../../../../@core/api';
import { NbDialogRef, NbCardModule, NbSpinnerModule, NbSelectModule, NbOptionModule, NbInputModule, NbButtonModule, NbToastrService } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { EnergyObjectivesService } from '../../../../@core/utils/energy-objectives.service';
import { PerimetersService } from '../../../../@core/utils/perimeters.service';
import { ChartsService } from '../../../../@core/utils/charts.service';
import { ScopeService } from '../../../../@core/utils/scope.service';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-add-carbon-objective-dialog',
  templateUrl: './add-carbon-objective-dialog.component.html',
  styleUrls: ['./add-carbon-objective-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbSelectModule,
    NbOptionModule,
    NbInputModule,
    NbButtonModule,
    AsyncPipe,
    TranslateModule
  ],
})
export class AddCarbonObjectiveDialogComponent implements OnInit, OnDestroy {
  @Input() public perimeter: KPI.Perimeter;
  @Input() public isEditing: boolean = false;

  public loading = false;
  private subscriptions: Subscription[] = [];
  public subPerimeters: any[] = [];
  public perimeters$: Observable<KPI.PerimeterConsumption[]>;
  public selectedPerimeterArea: number | null = null;
  private allPerimeters: KPI.Perimeter[] = [];

  public get isSelectedAreaInvalid(): boolean {
    if (this.selectedPerimeterArea === null || this.selectedPerimeterArea === undefined) {
      return true;
    }
    const area = parseFloat(`${this.selectedPerimeterArea}`);
    return !Number.isFinite(area) || area <= 0;
  }

  public get isObjectiveValueZero(): boolean {
    const objectiveValue = this.form.get('objective_value')?.value;
    if (objectiveValue === null || objectiveValue === undefined || objectiveValue === '') {
      return true;
    }
    const value = parseFloat(`${objectiveValue}`);
    return !Number.isFinite(value) || value === 0;
  }
  
  public form = new UntypedFormGroup({
    site: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    perimeter: new UntypedFormControl(null, [Validators.required]),
    area: new UntypedFormControl({ value: '0', disabled: true }, []),
    year: new UntypedFormControl('2030', [Validators.required]),
    objective_type: new UntypedFormControl({ value: this.translate.instant('Custom carbon objective for the site'), disabled: true }, [Validators.required]),
    objective_value: new UntypedFormControl(null, [Validators.required, Validators.min(0.01)]),
  });

  constructor(
    private dialogRef: NbDialogRef<AddCarbonObjectiveDialogComponent>,
    private energyObjectivesService: EnergyObjectivesService,
    private toastrService: NbToastrService,
    private translate: TranslateService,
    private perimetersService: PerimetersService,
    private chartsService: ChartsService,
    private scopeService: ScopeService
  ) { }

  ngOnInit(): void {
    this.form.get('site').setValue(this.perimeter.name);
    this.subPerimeters = this.perimeter.sub_perimeters.filter(sub=>!sub?.level_parent) || [];
    this.form.get('objective_type').setValue(this.translate.instant('Custom carbon objective for the site'));
    this.form.patchValue({ year: '2030' });
    
    this.perimeters$ = combineLatest([
      this.perimetersService.perimeters$,
      this.chartsService.getSiteConsumption(this.perimeter.id),
      this.scopeService.perimeterTypes$,
    ]).pipe(
      tap(([perimeters]) => {
        this.allPerimeters = perimeters.reduce((acc, p) => ([...acc, ...p.sub_perimeters]), [] as KPI.Perimeter[]);
      }),
      map(([perimeters, siteConsumption, perimeterTypes]) =>
        siteConsumption?.perimeters.filter((perimeter) => {
          const perimeterTypeId: number = perimeters
            .reduce((acc, p) => ([...acc, ...p.sub_perimeters]), [] as KPI.Perimeter[])
            .find((p) => p.id === perimeter.id)?.perimeter_type;
          return perimeterTypes[perimeterTypeId]?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING;
        }),
      ),
      tap((filteredPerimeters) => {
        if (filteredPerimeters && filteredPerimeters.length === 1) {
          this.form.get('perimeter').setValue(filteredPerimeters[0].id);
          this.updateAreaField(filteredPerimeters[0].id);
        }
      }),
    );

    this.subscriptions.push(
      this.form.get('perimeter').valueChanges.subscribe((perimeterId) => {
        if (perimeterId) {
          this.updateAreaField(perimeterId);
        } else {
          this.form.get('area').setValue('0');
          this.selectedPerimeterArea = null;
        }
      })
    );
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      const selectedPerimeterId = parseInt(this.form.get('perimeter').value);
      
      const payload = {
        site_id: this.perimeter.id,
        sub_perimeter_id: selectedPerimeterId,
        year: parseInt(this.form.get('year').value),
        emission: parseFloat(this.form.get('objective_value').value)
      };
      
      this.subscriptions.push(
        this.energyObjectivesService.createCarbonObjective(payload).subscribe({
          next: (result) => {
            this.loading = false;
            this.dialogRef.close(result);
          },
          error: (error) => {
            this.loading = false;
            console.error('Error creating carbon objective:', error);
            this.toastrService.danger(this.translate.instant('Error creating carbon objective'), this.translate.instant('Error'));
          }
        })
      );
    }
  }

  private updateAreaField(perimeterId: number): void {
    const selectedPerimeter = this.allPerimeters.find(p => p.id === perimeterId);
    if (selectedPerimeter && selectedPerimeter.flooring_area) {
      this.selectedPerimeterArea = selectedPerimeter.flooring_area;
      this.form.get('area').setValue(this.selectedPerimeterArea);
    } else {
      this.selectedPerimeterArea = null;
      this.form.get('area').setValue('0');
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
